import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard, CheckCircle, Clock, XCircle, Calendar, Zap, DollarSign,
  BookOpen, ArrowUpRight
} from "lucide-react";
import DrishtiSubscriptionCard from "@/components/drishti/DrishtiSubscriptionCard";

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("payments");

  const fetchData = useCallback(async () => {
    try {
      const [paymentsRes, subscriptionsRes] = await Promise.all([
        axios.get(`${API}/payments`, { withCredentials: true }),
        axios.get(`${API}/subscriptions`, { withCredentials: true })
      ]);
      setPayments(asArray(paymentsRes.data, "payments"));
      setSubscriptions(asArray(subscriptionsRes.data, "subscriptions"));
    } catch (error) {
      console.error("Error fetching payment data:", error); // eslint-disable-line no-console
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusConfig = {
    completed: { icon: CheckCircle, color: "#00FF88", label: "Completed" },
    pending: { icon: Clock, color: "#FFCC00", label: "Pending" },
    failed: { icon: XCircle, color: "#FF3B30", label: "Failed" },
  };

  if (loading) {
    return (
      <DashboardLayout title="Payments">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const totalSpent = payments.filter(p => p.status === "completed").reduce((s, p) => s + (p.amount || 0), 0);
  const totalPending = payments.filter(p => p.status === "pending").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <DashboardLayout title="Payments & Subscriptions">
      <div className="space-y-5" data-testid="payments-page">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="terminal-card rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-4 h-4 text-[#00FF88]" />
              <span className="text-[10px] font-jetbrains tracking-[0.15em] text-gray-400 dark:text-[#52525B] uppercase">TOTAL SPENT</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white font-jetbrains" data-testid="total-spent">₹{totalSpent.toLocaleString()}</p>
          </div>
          <div className="terminal-card rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-4 h-4 text-[#FFCC00]" />
              <span className="text-[10px] font-jetbrains tracking-[0.15em] text-gray-400 dark:text-[#52525B] uppercase">PENDING</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white font-jetbrains">₹{totalPending.toLocaleString()}</p>
          </div>
          <div className="terminal-card rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <CreditCard className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-[10px] font-jetbrains tracking-[0.15em] text-gray-400 dark:text-[#52525B] uppercase">TRANSACTIONS</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white font-jetbrains" data-testid="total-txns">{payments.length}</p>
          </div>
          <div className="terminal-card rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-4 h-4 text-[#A855F7]" />
              <span className="text-[10px] font-jetbrains tracking-[0.15em] text-gray-400 dark:text-[#52525B] uppercase">SUBSCRIPTIONS</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white font-jetbrains">{subscriptions.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {[{ key: "payments", label: "Payment History", icon: CreditCard }, { key: "subscriptions", label: "Subscriptions", icon: Zap }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-plex transition-all ${tab === t.key ? "bg-white/[0.06] text-gray-900 dark:text-white border border-gray-200 dark:border-white/10" : "text-[#52525B] hover:text-[#A1A1AA]"}`}
              data-testid={`tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Payment History */}
        {tab === "payments" && (
          <div className="terminal-card rounded-lg overflow-hidden" data-testid="payment-history">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-white/[0.06]">
              <p className="font-jetbrains text-xs tracking-[0.15em] text-[#00F0FF] uppercase">TRANSACTION LOG</p>
            </div>
            {payments.length === 0 ? (
              <div className="p-12 text-center">
                <CreditCard className="w-10 h-10 text-gray-400 dark:text-[#52525B] mx-auto mb-3" />
                <p className="text-gray-400 dark:text-[#52525B] font-plex">No payment history yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.03]">
                {payments.map((payment) => {
                  const st = statusConfig[payment.status] || statusConfig.pending;
                  const StIcon = st.icon;
                  return (
                    <div key={payment.payment_id} className="p-4 flex items-center gap-4 hover:bg-gray-50 dark:bg-white/[0.02] transition-colors" data-testid={`payment-${payment.payment_id}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-[${st.color}]/10`} style={{ backgroundColor: `${st.color}10` }}>
                        <CreditCard className="w-5 h-5" style={{ color: st.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-outfit font-semibold text-gray-900 dark:text-white truncate">{payment.description || payment.payment_type}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Calendar className="w-3 h-3 text-gray-400 dark:text-[#52525B]" />
                          <span className="text-xs text-gray-400 dark:text-[#52525B] font-jetbrains">{new Date(payment.created_at).toLocaleDateString()}</span>
                          <span className="text-xs text-gray-400 dark:text-[#52525B] font-jetbrains">|</span>
                          <span className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">{payment.payment_id}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 dark:text-white font-jetbrains">₹{(payment.amount || 0).toLocaleString()}</p>
                        <Badge className="border-0 text-[10px] font-jetbrains" style={{ backgroundColor: `${st.color}15`, color: st.color }}>
                          <StIcon className="w-3 h-3 mr-1" />{st.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Subscriptions */}
        {tab === "subscriptions" && (
          <div className="space-y-4">
            <DrishtiSubscriptionCard />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="terminal-card rounded-lg p-6" data-testid="ai-tools-subscription">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00FF88]/10 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#00FF88]" />
                  </div>
                  <div>
                    <h3 className="text-gray-900 dark:text-white font-outfit font-semibold">AI Trading Tools</h3>
                    <Badge className="bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-[#52525B] border-0 text-[10px]">Coming Soon</Badge>
                  </div>
                </div>
                <p className="text-sm text-gray-400 dark:text-[#52525B] font-plex mb-4">Access advanced AI-powered trading tools, market analysis, and automated strategies.</p>
                <Button disabled className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-400 dark:text-[#52525B] rounded-sm">Notify Me</Button>
              </div>

              <div className="terminal-card rounded-lg p-6" data-testid="drishti-subscription">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <div>
                    <h3 className="text-gray-900 dark:text-white font-outfit font-semibold">Drishti Alerts</h3>
                    <Badge className="bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-[#52525B] border-0 text-[10px]">Coming Soon</Badge>
                  </div>
                </div>
                <p className="text-sm text-gray-400 dark:text-[#52525B] font-plex mb-4">Real-time market alerts, price notifications, and trading signals.</p>
                <Button disabled className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-400 dark:text-[#52525B] rounded-sm">Notify Me</Button>
              </div>
            </div>

            {subscriptions.length > 0 && (
              <div className="terminal-card rounded-lg p-4">
                <p className="font-jetbrains text-xs tracking-[0.15em] text-[#00FF88] uppercase mb-3">ACTIVE SUBSCRIPTIONS</p>
                <div className="space-y-3">
                  {subscriptions.map(sub => (
                    <div key={sub.subscription_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-white/[0.02] rounded-lg">
                      <div>
                        <p className="text-sm font-outfit font-semibold text-gray-900 dark:text-white capitalize">{sub.type}</p>
                        <p className="text-xs text-gray-400 dark:text-[#52525B] font-jetbrains">Expires: {new Date(sub.end_date).toLocaleDateString()}</p>
                      </div>
                      <Badge className="bg-[#00FF88]/10 text-[#00FF88] border-0 text-xs capitalize">{sub.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PaymentsPage;
